/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react'
import { BlogSlider } from '@components/blog/BlogSlider'
import Sidebar from '@components/blog/Sidebar'
import BottomSection from '@components/blog/BottomSection'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { Layout } from '@components/common'
import s from '@pagestyles/Blog.module.scss'
import cn from 'classnames'
import { Button } from '@corratech/pylot-ui'
import { Breadcrumbs } from '@components/alfakher/pylot-breadcrumbs'
import { SinglePostData } from '@pylot-data/api/operations/get-singlepost'
import getBlog, { BlogPostsResult } from '@pylot-data/api/operations/get-blog'
import {
    authorName,
    authorTime,
    authorTitleUrl,
    generateBlogIdentifier
} from '@components/common/CMSBlockConst/cms-block-util'
import { RichContent } from '@corratech/pylot-rich-content/index'
import { SEO, SEO_ENUMS } from '@components/seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
    GetStaticProps,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import getBlogLandingSeoDetails from '@pylot-data/api/operations/get-blog-landing-seo-details'
import SearchPost from '@components/blog/SearchPost'
import { useBlogPageSeo } from '@pylot-data/hooks/blog/use-blog-page-seo'
import { useRouter } from 'next/router'
import { IS_BLOG_ENABLED } from '@components/StoreConfiguration/StoreConfiguration'

export const getStaticProps: GetStaticProps = async ({
    locale
}: GetStaticPropsContext) => {
    const blogData = await getBlog({
        currentPage: 1,
        pageSize: 6,
        locale: locale || '',
        sortFiled: 'modified_time'
    })
    if (!blogData) {
        return {
            notFound: true
        }
    }
    const blogSeoDetails = await getBlogLandingSeoDetails({ locale })
    return {
        props: {
            posts: blogData.blogPosts,
            blogSeo: blogSeoDetails?.storeConfig,
            ...(await serverSideTranslations(locale!, ['common', 'blog']))
        },
        revalidate: 14400
    }
}

export default function BlogPage({
    posts,
    blogSeo
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
    const { t } = useTranslation(['common', 'blog'])
    const [numberCount, setNumberCount] = useState<number>(1)
    const [details, setDetails] = useState<BlogPostsResult | null>(null)
    const posturllink = '/blog/'
    const { blogPage = blogSeo } = useBlogPageSeo({
        revalidateOnFocus: false,
        revalidateOnMount: true,
        initialData: blogSeo
    })
    const router = useRouter()
    const { locale } = router
    useEffect(() => {
        const fetchDatamain = async () => {
            const blogData = await getBlog({
                currentPage: numberCount,
                pageSize: 8,
                locale: locale || '',
                sortFiled: 'modified_time'
            })

            if (blogData?.blogPosts?.items) {
                if (details?.items) {
                    const updatedItems = [
                        ...(details.items || []),
                        ...(blogData.blogPosts.items || [])
                    ]
                    setDetails({ ...details, items: updatedItems })
                } else {
                    setDetails(blogData.blogPosts)
                }
                posts = blogData.blogPosts
            }
        }
        fetchDatamain()
    }, [numberCount])

    const moreDetailsClick = () => {
        setNumberCount(numberCount + 1)
    }

    return (
        <div className={s['blog-container']}>
            {blogPage && (
                <SEO
                    blogLanding={blogPage}
                    page={SEO_ENUMS.BHome}
                    blogBreadCrumbs={{ name: [] }}
                />
            )}
            <div className={s['container-classes']}>
                <div className={s['breadcrumb-inner']}>
                    <div className={s['breadcrumb-wrapper']}>
                        <Breadcrumbs
                            category={{
                                name: t('Blog')
                            }}
                            rootText={t('breadcrumbs|Home')}
                            separator=" | "
                        />
                    </div>
                </div>
                <div className={s['toggleSearchBar']}>
                    <SearchPost />
                </div>
                {IS_BLOG_ENABLED && <BlogSlider sliderdetails={posts} />}
                <div className={s['fullContent']}>
                    <div className={s['leftSection']}>
                        <div className={s['mainPost']}>
                            {details?.items?.length &&
                                details.items.map((post: SinglePostData) => {
                                    return (
                                        <div key={post.post_id}>
                                            <div
                                                className={s['postList']}
                                                key={post.post_id}
                                            >
                                                <div className={s['postImage']}>
                                                    <Link
                                                        href={`${posturllink}${post.identifier}`}
                                                    >
                                                        <img
                                                            src={
                                                                post.featured_list_image
                                                            }
                                                            alt={post.title}
                                                            title={post.title}
                                                        />
                                                    </Link>
                                                </div>
                                                <div className={s['postTitle']}>
                                                    <Link
                                                        href={`${posturllink}${post.identifier}`}
                                                    >
                                                        <a>{post.title}</a>
                                                    </Link>
                                                </div>
                                                <h5>
                                                    {post.author && (
                                                        <>
                                                            {t(`By`)}{' '}
                                                            <Link
                                                                href={`${posturllink}author/${generateBlogIdentifier(
                                                                    post.author
                                                                        ?.name
                                                                )}/${
                                                                    post?.author
                                                                        ?.author_id
                                                                }`}
                                                            >
                                                                <a>
                                                                    {authorName(
                                                                        post
                                                                    )}
                                                                </a>
                                                            </Link>
                                                        </>
                                                    )}
                                                    {authorTime(post)}
                                                    <RichContent
                                                        html={authorTitleUrl(
                                                            post
                                                        )}
                                                    />
                                                </h5>
                                                <div
                                                    className={
                                                        s['clampWrapper']
                                                    }
                                                >
                                                    <div className={s['clamp']}>
                                                        <RichContent
                                                            html={
                                                                post.short_filtered_content ||
                                                                ''
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className={s['readmoreUrl']}
                                                >
                                                    <Link
                                                        href={`${posturllink}${post.identifier}`}
                                                    >
                                                        {t('Read More...')}
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            {numberCount != details?.total_pages ? (
                                <div className={s['fullLoadmore']}>
                                    <Button
                                        variant="secondary"
                                        className={cn(s['load-more'])}
                                        onClick={() => moreDetailsClick()}
                                    >
                                        {t('LOAD MORE')}
                                    </Button>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    </div>
                    <Sidebar />
                </div>
            </div>
            <BottomSection />
        </div>
    )
}

BlogPage.Layout = Layout
